import { createContext, FC, PropsWithChildren, useCallback, useContext, useMemo } from 'react';
import { UseQueryExecute } from 'urql';
import { handleMutationResult } from '../../helpers/toastHelper';
import { FIXED_LEAVE_TYPES, FixedLeaveTypeId } from '../availability/availability.types';
import {
  ReadLeaveType,
  useCreateLeaveTypeMutation,
  useCreateLeaveTypesMutation,
  useDeleteLeaveTypesMutation,
  useGetLeaveTypesQuery,
  useUpdateLeaveTypeMutation,
  useUpdateLeaveTypesMutation
} from '../gql/graphql.generated';
import { useSystemSettings } from '../settings/SystemSettingsProvider';
import { LeaveType } from './leave.types';
import { convertFromGqlLeaveTypes, convertLeaveTypeToGql, patchUpTapNumbers } from './leaveManagementHelper';

export interface LeaveTypesStore {
  leaveTypes: LeaveType[];
  refetch: UseQueryExecute;
}

export const LeaveTypesContext = createContext<LeaveTypesStore>({ leaveTypes: [], refetch: () => {} });

export const LeaveTypesProvider: FC<PropsWithChildren> = ({ children }) => {
  const [{ data }, refetch] = useGetLeaveTypesQuery();
  const advanced = useSystemSettings(state => state.settings.advancedLeaveManagement);

  const store = useMemo(() => {
    const leaveTypesData = data?.leaveTypes || [];

    const rosterDayOff = leaveTypesData.find(lt => lt.id === FixedLeaveTypeId.ROSTER);
    const paidLeave = leaveTypesData.find(lt => lt.id === FixedLeaveTypeId.PAID);

    if (!rosterDayOff || !paidLeave) {
      throw Error('Unable to get Paid and RDO leave types, which must exist in all SnpsShift tenant databases');
    }

    const basicLeaveTypes: ReadLeaveType[] = [rosterDayOff, paidLeave];

    let leaveTypes: ReadLeaveType[];
    if (advanced) {
      const advancedTypes = leaveTypesData.filter(l => !FIXED_LEAVE_TYPES.includes(l.id));
      leaveTypes = [...basicLeaveTypes, ...advancedTypes];
    } else {
      leaveTypes = basicLeaveTypes;
    }

    return { leaveTypes: convertFromGqlLeaveTypes(leaveTypes), refetch };
  }, [data, advanced, refetch]);

  return <LeaveTypesContext.Provider value={store}>{children}</LeaveTypesContext.Provider>;
};

export const useLeaveTypes = () => {
  const context = useContext(LeaveTypesContext);

  if (context.leaveTypes.length === 0) {
    throw Error('useLeaveManagement must be used within a LeaveTypesProvider');
  }

  return context.leaveTypes;
};

export const useRefreshLeaveTypes = () => {
  const context = useContext(LeaveTypesContext);

  return context.refetch;
};

export const useEssLeaveTypes = () => {
  const leaveTypes = useLeaveTypes();

  return useMemo(() => {
    const filteredTypes = leaveTypes.filter(lt => {
      let valid = lt.active;

      if (!(FIXED_LEAVE_TYPES.includes(lt.id) || !lt.properties.managerOnly)) {
        valid = false;
      }

      return valid;
    });
    const sorted = filteredTypes.sort((a, b) => {
      const aTap = a.properties.tapNumber;
      const bTap = b.properties.tapNumber;

      if (aTap === -1) {
        return -1;
      }
      if (bTap === -1) {
        return 1;
      } else {
        return aTap - bTap;
      }
    });

    return patchUpTapNumbers(sorted);
  }, [leaveTypes]);
};

export const useLeaveType = (leaveTypeId?: string) => {
  const leaveTypes = useLeaveTypes();

  const leaveType = leaveTypes.find(lt => lt.id === leaveTypeId);

  if (leaveTypeId && !leaveType) {
    throw Error(`Could not find leave type ${leaveTypeId}`);
  }

  return leaveType;
};

export const useGetLeaveType = () => {
  const leaveTypes = useLeaveTypes();

  return useCallback(
    (leaveTypeId: string) => {
      const leaveType = leaveTypes.find(l => l.id === leaveTypeId);

      return leaveType;
    },
    [leaveTypes]
  );
};

export const useCreateLeaveTypes = () => {
  const [{ fetching }, create] = useCreateLeaveTypesMutation();
  const createLeaveTypes = useCallback(
    async (leaveTypes: LeaveType[]) => {
      const result = await create({ leaveTypes: leaveTypes.map(lt => convertLeaveTypeToGql(lt, true)) });
      await handleMutationResult(result, {
        successMessage: 'Leave types saved successfully.',
        errorMessage: 'Failed to save leave types.'
      });

      return result;
    },
    [create]
  );

  return { fetching, createLeaveTypes };
};

export const useCreateLeaveType = () => {
  const [{ fetching }, create] = useCreateLeaveTypeMutation();
  const createLeaveType = useCallback(
    async (leaveType: LeaveType) => {
      const result = await create({ leaveType: convertLeaveTypeToGql(leaveType, true) });

      return result;
    },
    [create]
  );

  return { fetching, create: createLeaveType };
};

export const useUpdateLeaveTypes = () => {
  const [{ fetching }, update] = useUpdateLeaveTypesMutation();
  const updateLeaveTypes = useCallback(
    async (leaveTypes: LeaveType[]) => {
      const result = await update({ leaveTypes: leaveTypes.map(lt => convertLeaveTypeToGql(lt)) });
      await handleMutationResult(result, {
        successMessage: 'Leave types saved successfully.',
        errorMessage: 'Failed to save leave types.'
      });

      return result;
    },
    [update]
  );

  return { fetching, updateLeaveTypes };
};

export const useUpdateLeaveType = () => {
  const [{ fetching }, update] = useUpdateLeaveTypeMutation();
  const updateLeaveTypes = useCallback(
    async (leaveType: LeaveType) => {
      const result = await update({ leaveType: convertLeaveTypeToGql(leaveType) });

      return result;
    },
    [update]
  );

  return { fetching, update: updateLeaveTypes };
};

export const useDeleteLeaveTypes = () => {
  const [{ fetching }, mutation] = useDeleteLeaveTypesMutation();

  const deleteLeaveTypes = useCallback(
    async (ids: string[]) => {
      const idsToDelete = ids.filter(id => !FIXED_LEAVE_TYPES.includes(id));

      const result = await mutation({ ids: idsToDelete });
      await handleMutationResult(result, {
        successMessage: 'Leave types deleted successfully.',
        errorMessage: 'Failed to delete leave types.'
      });

      return result;
    },
    [mutation]
  );

  return { deleteLeaveTypes, fetching };
};
